import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback, useEffect, useState } from "react";

import styled from "styled-components/macro";

import { faCalendarDay, faRepeat } from "@fortawesome/pro-duotone-svg-icons";
import { shouldShowNeedsUpdating } from "Payments/Shared/shouldShowNeedsUpdating";
import { motion } from "framer-motion";

import FnrPaymentMethodIcon from "UI/FnrPaymentMethodIcon";
import { colors } from "UI/colors";

import { formatAmountInCentsForDisplay } from "Utilities/Number/formatAmountInCentsForDisplay";

import {
  editModalPanels,
  useRecurringGifts,
} from "../../RecurringGiftsContext";
import { formatDateString } from "../../Shared/formatDateString";
import { formatTimestamp } from "../../Shared/formatTimestamp";
import FundNameTopArea from "../FundNameTopArea";

import DeleteButton from "./DeleteButton";
import EditButton from "./EditButton";
import NotificationMsg from "./NotificationMsg";
import PauseButton from "./PauseButton";
import PaymentMethod from "./PaymentMethod";
import SkipButton from "./SkipButton";
import UpdateNotice from "./UpdateNotice";

export default function InitialPanel({ closeModal }) {
  const { state, dispatch } = useRecurringGifts();

  const {
    paymentType,
    amount,
    interval,
    endDate,
    paymentMethod,
    paymentDates,
    futureSkippedPayments,
    userCanEdit,
    userCanEditPaymentMethods,
    status,
    memo,
  } = state.editModal?.currentGift || {};

  const { memoEnabled } = state.editModal?.giftDetails?.fund;
  const { duplicatePaymentForTodayError } = state;

  const { cardType } = paymentMethod || {};
  const paymentNoun = paymentType
    ? paymentType[0].toUpperCase() + paymentType.slice(1)
    : "Payment";

  const nextPaymentDate = paymentDates ? paymentDates[0] : null;
  const [nextPaymentTrigger, setNextPaymentTrigger] = useState(0);
  const [noticeMessage, setNoticeMessage] = useState(null);

  const showUpdateNotice = shouldShowNeedsUpdating(paymentMethod);

  const triggerNextPaymentAnimation = () => {
    setNextPaymentTrigger((prev) => prev + 1);
  };

  // const getNextPaymentDateToDisplay = (includeYear = true) => {
  //   if (status !== "active") return "N/A";
  //   return formatDateString(nextPaymentDate, { includeYear });
  // };

  const getNextPaymentDateToDisplay = useCallback(
    (includeYear = true) => {
      if (status !== "active") return "N/A";
      return formatDateString(nextPaymentDate, { includeYear });
    },
    [nextPaymentDate, status]
  );

  useEffect(() => {
    setNoticeMessage(
      duplicatePaymentForTodayError
        ? `Your updates have been saved! Since your ${paymentNoun} has already processed earlier today, no further ${paymentNoun} will be processed until ${getNextPaymentDateToDisplay(
            false
          )}.`
        : null
    );
  }, [duplicatePaymentForTodayError, paymentNoun, getNextPaymentDateToDisplay]);

  const getSkippedPayments = () => {
    if (!futureSkippedPayments?.length || status !== "active") return null;

    // I recoginize this is ugly code. Feel free to make it elegant.
    const getPaymentDateString = () => {
      if (futureSkippedPayments.length === 1) {
        return formatDateString(futureSkippedPayments[0]);
      } else if (futureSkippedPayments.length === 2) {
        return (
          formatDateString(futureSkippedPayments[0]) +
          " & " +
          formatDateString(futureSkippedPayments[1])
        );
      } else if (futureSkippedPayments.length === 3) {
        return (
          formatDateString(futureSkippedPayments[0]) +
          ", " +
          formatDateString(futureSkippedPayments[1]) +
          " & " +
          formatDateString(futureSkippedPayments[2])
        );
      } else {
        return (
          formatDateString(futureSkippedPayments[0]) +
          ", " +
          formatDateString(futureSkippedPayments[1]) +
          ", " +
          formatDateString(futureSkippedPayments[2]) +
          " & more"
        );
      }
    };

    return `Skipped ${paymentNoun}s: ${getPaymentDateString()}`;
  };

  const handleEditButtonClick = () => {
    dispatch({
      type: "setCurrentPanel",
      payload: editModalPanels.edit,
    });
    setNoticeMessage(null);
  };

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { when: "beforeChildren", staggerChildren: 0.1 },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 15 },
    show: { opacity: 1, y: 0 },
  };

  return (
    <Wrapper>
      <FundNameTopArea />
      <>
        <NotificationMsg />

        <GiftInfoBox>
          {userCanEditPaymentMethods && (
            <EditButtonWrapper>
              <EditButton onClick={handleEditButtonClick} />
            </EditButtonWrapper>
          )}

          <Section variants={item}>
            <Icon>
              <FontAwesomeIcon icon={faRepeat} />
            </Icon>
            <Info
              data-cy="recurringInfo"
              style={{ paddingTop: endDate ? 0 : "5px" }}
            >
              <Amount data-cy="recurringAmountInfo">
                ${formatAmountInCentsForDisplay(amount)}
              </Amount>{" "}
              / {interval?.toLowerCase()}
              {endDate ? (
                <EndDate>until {formatTimestamp(endDate)}</EndDate>
              ) : null}
              {memoEnabled && memo ? (
                <div style={{ marginTop: endDate ? 0 : "14px" }}>
                  <Memo>
                    <strong>Memo:</strong> {memo}
                  </Memo>
                </div>
              ) : null}
            </Info>
          </Section>

          <Section
            style={{ marginTop: endDate ? "-10px" : null }}
            variants={item}
          >
            <Icon>
              <FnrPaymentMethodIcon cardType={cardType} />
            </Icon>

            <PaymentMethodWrapper>
              <PaymentMethod paymentMethod={paymentMethod} />
              {showUpdateNotice && (
                <UpdateNotice setNoticeMessage={setNoticeMessage} />
              )}
            </PaymentMethodWrapper>
          </Section>

          <Section variants={item}>
            <Icon style={{ background: "#a12af2" }}>
              <FontAwesomeIcon icon={faCalendarDay} />
            </Icon>
            <NextPayment>
              Next {paymentNoun}:{" "}
              <NextPaymentDate
                data-cy="recurringNextPaymentDate"
                initial={
                  nextPaymentTrigger
                    ? {
                        opacity: 0,
                        rotateX: 180,
                        scale: [0.5],
                      }
                    : {}
                }
                animate={
                  nextPaymentTrigger
                    ? {
                        opacity: 1,
                        rotateX: 0,
                        scale: [0.5, 1.2, 1],
                      }
                    : {}
                }
                transition={{ duration: 0.3 }}
                key={nextPaymentTrigger}
              >
                {getNextPaymentDateToDisplay()}
              </NextPaymentDate>
              <SkippedPayments>{getSkippedPayments()}</SkippedPayments>
            </NextPayment>
          </Section>
        </GiftInfoBox>

        {noticeMessage && <Message>{noticeMessage}</Message>}

        {userCanEdit ? (
          <>
            <Buttons variants={container} initial="hidden" animate="show">
              <ButtonWrapper variants={item}>
                <SkipButton
                  triggerAnimation={triggerNextPaymentAnimation}
                  setNoticeMessage={setNoticeMessage}
                />
              </ButtonWrapper>

              <ButtonWrapper variants={item}>
                <PauseButton setNoticeMessage={setNoticeMessage} />
              </ButtonWrapper>

              <ButtonWrapper variants={item}>
                <DeleteButton closeModal={closeModal} />
              </ButtonWrapper>
            </Buttons>
          </>
        ) : null}
      </>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  padding: 20px;
`;

const GiftInfoBox = styled(motion.div)`
  box-shadow: 0 8px 24px 0 #eeeeeeff, 0 4px 8px 0 #42474c0f, 0 0 1px 0 #42474c52;
  padding: 20px;
  border-radius: 14px;
  color: #474747;
  position: relative;

  @media screen and (max-width: 600px) {
    display: flex;
    flex-direction: column;
  }
`;

const EditButtonWrapper = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;

  @media screen and (max-width: 600px) {
    position: relative;
    top: 0;
    right: 0;
    width: min-content;
    margin: 10px auto 0 auto;
    order: 100;
  }
`;

const Section = styled(motion.div)`
  display: flex;
  align-items: center;
  margin-bottom: 18px;

  &:last-of-type {
    margin-bottom: 0;
  }
`;

const PaymentMethodWrapper = styled.div`
  display: grid;
`;

const Icon = styled.div`
  align-self: flex-start;
  width: 42px;
  height: 42px;
  flex-shrink: 0;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  font-size: 24px;
  margin-right: 14px;
  background: rgb(0, 180, 78);
  position: relative;
  overflow: hidden;
`;

const Info = styled.div`
  font-size: 24px;
  line-height: 28px;
  font-weight: 400;
`;

const Amount = styled.span`
  font-weight: 600;
  font-size: 30px;
`;

const EndDate = styled.div`
  font-size: 16px;
  opacity: 0.9;
`;

const Memo = styled.div`
  font-size: 12px;
  opacity: 0.9;
  line-height: 16px;
  margin-top: 2px;
`;

const NextPayment = styled(motion.div)`
  font-weight: 600;
`;

const Buttons = styled(motion.div)`
  display: flex;
  justify-content: space-between;
  margin-top: 14px;

  @media (max-width: 500px) {
    flex-direction: column;
  }
`;

const NextPaymentDate = styled(motion.div)`
  display: inline-block;
  white-space: nowrap;
`;

const SkippedPayments = styled.div`
  font-weight: 400;
  font-size: 14px;
`;

const ButtonWrapper = styled(motion.div)`
  flex: 1;
  margin-right: 10px;

  &:last-of-type {
    margin-right: 0;
  }

  @media (max-width: 500px) {
    margin: 10px 0;
  }
`;

const Message = styled.div`
  color: ${colors.fnRedNotice};
  margin: 6px 0;
  text-align: center;
`;
